import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import UserModal from '../modals/UserModal';
import UserViewModal from '../modals/UserViewModal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import PageHeader from '../common/PageHeader';
import Card from '../common/Card';
import Button from '../common/Button';
import Badge from '../common/Badge';
import EmptyState from '../common/EmptyState';
import FilterBar from '../common/FilterBar';
import { TableSkeleton } from '../common/Skeleton';
import { Table, THead, Th, Tr, Td } from '../common/Table';
import useSortableData from '../../hooks/useSortableData';

const UsersPage = ({ onNavigate }) => {
  const { isAdmin, backendOnline } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
      setError('You do not have permission to view users');
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load users';
      setError(errorMessage);

      // If unauthorized, user might have lost admin access
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('You do not have permission to view users');
      } else if (error.isNetworkError) {
        setError('ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    // Navigate to create user page instead of opening modal
    if (onNavigate) {
      onNavigate('create-user');
    } else {
      // Fallback to modal if navigate function not available
      setEditingUser(null);
      setModalOpen(true);
    }
  };

  const handleEdit = async (user) => {
    try {
      // Fetch full user details including permissions
      const response = await api.get(`/users/${user.id}`);
      setEditingUser(response.data.user);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Fallback to using user from list if fetch fails
      setEditingUser(user);
      setModalOpen(true);
    }
  };

  const handleView = async (user) => {
    try {
      const response = await api.get(`/users/${user.id}/view`);
      setViewingUser(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load user details');
    }
  };

  const handleDelete = async (userId, userRoles) => {
    // Prevent deleting admin users - check on frontend as well
    const isAdminUser = Array.isArray(userRoles) && userRoles.some(role => role.toLowerCase() === 'admin');

    if (isAdminUser) {
      toast.error('Cannot delete admin users. Admin users are protected and cannot be deleted.');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete this user?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to delete user');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingUser(null);
    fetchUsers();
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter(user =>
      (user.name || '').toLowerCase().includes(term) ||
      (user.phone || '').toLowerCase().includes(term) ||
      (user.shop_name || '').toLowerCase().includes(term) ||
      (user.shop_code || '').toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const { sortedItems: sortedUsers, sortConfig, requestSort } = useSortableData(filteredUsers, null, (row, key) => {
    if (key === 'roles') return Array.isArray(row.roles) ? row.roles.join(', ') : '';
    return row[key];
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-2 sm:px-3">
        <PageHeader icon="fa-users" title="User Management" subtitle="Manage system users and their access permissions" />
        <Card padding="none" className="overflow-hidden">
          <TableSkeleton rows={5} columns={6} />
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-2 sm:px-3">
        <Card className="!border-red-200 dark:!border-red-500/30 bg-red-50 dark:bg-red-500/10 text-center">
          <i className="fas fa-exclamation-triangle text-red-500 dark:text-red-400 text-3xl mb-3" aria-hidden="true"></i>
          <p className="text-red-700 dark:text-red-400 font-semibold">You do not have permission to view users</p>
        </Card>
      </div>
    );
  }

  if (error && !backendOnline) {
    return (
      <div className="max-w-7xl mx-auto px-2 sm:px-3">
        <Card className="!border-amber-200 dark:!border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-center">
          <i className="fas fa-exclamation-triangle text-amber-500 dark:text-amber-400 text-3xl mb-3" aria-hidden="true"></i>
          <p className="text-amber-700 dark:text-amber-400 font-semibold mb-4">{error}</p>
          <Button onClick={fetchUsers}>Retry</Button>
        </Card>
      </div>
    );
  }

  const isAdminRole = (roles) => Array.isArray(roles) && roles.some(role => role.toLowerCase() === 'admin');

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-3">
      <PageHeader
        icon="fa-users"
        title="User Management"
        subtitle="Manage system users and their access permissions"
        actions={<Button icon="fa-plus" onClick={handleCreate}>Create User</Button>}
      />

      {error && (
        <Card className="!border-red-200 dark:!border-red-500/30 bg-red-50 dark:bg-red-500/10 mb-4" padding="sm">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          <Button variant="danger" size="sm" className="mt-3" onClick={fetchUsers}>Retry</Button>
        </Card>
      )}

      {users.length > 0 && (
        <FilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by name, phone, shop..."
          hasActiveFilters={!!searchTerm}
          onClear={() => setSearchTerm('')}
          className="mb-4"
        />
      )}

      <Card padding="none" className="overflow-hidden">
        {users.length === 0 ? (
          <EmptyState icon="fa-users" title="No users found" description="Create your first user to get started" />
        ) : sortedUsers.length === 0 ? (
          <EmptyState icon="fa-search" title="No matching users" description="Try a different search term" />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table>
                <THead>
                  <tr>
                    <Th sortable sortKey="name" currentSort={sortConfig} onSort={requestSort}>Name</Th>
                    <Th sortable sortKey="phone" currentSort={sortConfig} onSort={requestSort}>Phone Number</Th>
                    <Th sortable sortKey="shop_name" currentSort={sortConfig} onSort={requestSort}>Shop Name</Th>
                    <Th>Shop Code</Th>
                    <Th sortable sortKey="roles" currentSort={sortConfig} onSort={requestSort}>Roles</Th>
                    <Th>Actions</Th>
                  </tr>
                </THead>
                <tbody>
                  {sortedUsers.map((user) => (
                    <Tr key={user.id}>
                      <Td className="font-semibold text-slate-800 dark:text-slate-100">{user.name}</Td>
                      <Td>
                        <span className="flex items-center gap-2">
                          <i className="fas fa-phone text-primary dark:text-accent text-xs" aria-hidden="true"></i>
                          {user.phone}
                        </span>
                      </Td>
                      <Td>{user.shop_name || <span className="text-slate-400 dark:text-slate-500 italic">-</span>}</Td>
                      <Td>{user.shop_code || <span className="text-slate-400 dark:text-slate-500 italic">-</span>}</Td>
                      <Td>
                        <div className="flex gap-1.5 flex-wrap">
                          {Array.isArray(user.roles) && user.roles.length > 0 ? (
                            user.roles.map((role, idx) => <Badge key={idx} color="primary">{role}</Badge>)
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500 italic">-</span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="secondary" icon="fa-eye" onClick={() => handleView(user)} aria-label={`View ${user.name}`}>View</Button>
                          {!isAdminRole(user.roles) && (
                            <>
                              <Button size="sm" icon="fa-edit" onClick={() => handleEdit(user)} aria-label={`Edit ${user.name}`}>Edit</Button>
                              <Button size="sm" variant="danger" icon="fa-trash" onClick={() => handleDelete(user.id, user.roles)} aria-label={`Delete ${user.name}`}>Delete</Button>
                            </>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-700">
              {sortedUsers.map((user) => (
                <div key={user.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-slate-800 dark:text-slate-100">{user.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <i className="fas fa-phone text-primary dark:text-accent text-xs" aria-hidden="true"></i>{user.phone}
                      </p>
                    </div>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      {Array.isArray(user.roles) && user.roles.map((role, idx) => <Badge key={idx} color="primary">{role}</Badge>)}
                    </div>
                  </div>
                  {(user.shop_name || user.shop_code) && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                        <div className="text-xs text-slate-400 dark:text-slate-500">Shop Name</div>
                        <div className="font-medium text-slate-700 dark:text-slate-200">{user.shop_name || '-'}</div>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                        <div className="text-xs text-slate-400 dark:text-slate-500">Shop Code</div>
                        <div className="font-medium text-slate-700 dark:text-slate-200">{user.shop_code || '-'}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap pt-1">
                    <Button size="sm" variant="secondary" icon="fa-eye" onClick={() => handleView(user)} className="flex-1">View</Button>
                    {!isAdminRole(user.roles) && (
                      <>
                        <Button size="sm" icon="fa-edit" onClick={() => handleEdit(user)} className="flex-1">Edit</Button>
                        <Button size="sm" variant="danger" icon="fa-trash" onClick={() => handleDelete(user.id, user.roles)} className="flex-1">Delete</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {modalOpen && (
        <UserModal
          user={editingUser}
          onClose={handleModalClose}
        />
      )}

      {viewingUser && (
        <UserViewModal
          user={viewingUser}
          onClose={() => setViewingUser(null)}
        />
      )}
    </div>
  );
};

export default UsersPage;
